import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Wheat, BookOpen, Settings2, Image, LogOut, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import LeadsManager from "@/pages/admin/LeadsManager";
import ProductsManager from "@/pages/admin/ProductsManager";
import BlogManager from "@/pages/admin/BlogManager";
import ContentManager from "@/pages/admin/ContentManager";
import MediaManager from "@/pages/admin/MediaManager";

const TABS = [
  { id: "leads", label: "Leads & Orders", icon: LayoutDashboard },
  { id: "products", label: "Products", icon: Wheat },
  { id: "blog", label: "Blog", icon: BookOpen },
  { id: "content", label: "FAQs & Settings", icon: Settings2 },
  { id: "media", label: "Media Library", icon: Image },
];

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("leads");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  if (user === null) {
    return (
      <div className="min-h-screen bg-brand-ivory flex items-center justify-center">
        <Loader2 className="w-7 h-7 text-brand-deep animate-spin" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    toast.success("Signed out.");
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-brand-beige/50">
      <header className="sticky top-0 z-40 bg-brand-ivory/90 backdrop-blur-md border-b border-brand-deep/10">
        <div className="px-4 sm:px-8 max-w-7xl mx-auto flex items-center justify-between h-16">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-brand-charcoal/50">{user.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-full border border-brand-deep/20 px-4 py-2 text-xs font-bold text-brand-deep hover:bg-brand-deep hover:text-white transition-all"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="px-4 sm:px-8 max-w-7xl mx-auto py-8">
        <div className="flex gap-2 overflow-x-auto pb-2 mb-8" role="tablist">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-bold transition-all ${
                tab === id ? "bg-brand-deep text-white shadow-lg shadow-brand-deep/20" : "bg-white text-brand-deep/60 border border-brand-deep/15 hover:border-brand-deep/40"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "leads" && <LeadsManager />}
        {tab === "products" && <ProductsManager />}
        {tab === "blog" && <BlogManager />}
        {tab === "content" && <ContentManager />}
        {tab === "media" && <MediaManager />}
      </div>
    </div>
  );
}
